/**
 * BLRRD Auto-Tone — Phase 4-bonus production feature.
 *
 * Analyses a Float32 ACEScg image and returns suggested adjustment
 * values that bring the median luminance to ~0.18 (perceptual middle
 * grey), the 5–95th percentile range to a tasteful display target, and
 * (optionally) corrects a global colour cast via gray-world / surface-
 * detection white balance. The legacy editor's "Auto-Tone" toolbar
 * button (keyboard shortcut `A`) can be re-wired to call this module
 * once the surrounding UI work lands; for now the module is exposed via
 * `window.BlrrdAutoTone` for power users + the test gate.
 *
 * Algorithm (clean-room from public papers):
 *
 *   1. Build a 256-bin luminance histogram on Y = 0.2126·R + 0.7152·G
 *      + 0.0722·B (Rec.709 weights) — cheap O(N) scan.
 *   2. Compute percentile-5 (P05) + median (P50) + percentile-95 (P95)
 *      + percentile-99 (P99). The well-known Reinhard 2002 SIGGRAPH
 *      "Photographic Tone Reproduction for Digital Images" maps the
 *      log-average luminance to 0.18; we use median as a slightly more
 *      robust proxy under heavy outliers (specular highlights).
 *   3. Suggest exposure EV: target P50 → 0.18. ΔEV = log2(0.18 / P50).
 *   4. Suggest contrast: target P05 → 0.05, P95 → 0.85. Computed via
 *      the linear-scale factor that maps (P05, P95) onto the target
 *      range AFTER the exposure shift is applied. Clamped to [-100,
 *      +100] in legacy-slider units.
 *   5. Suggest saturation: compute mean chroma (max-min per pixel)
 *      across a stratified sample of 1024 pixels (cheap). Compare
 *      against a tasteful reference (0.15 — derived from ColorChecker
 *      24-patch chart's average). Boost if below, reduce if above.
 *   6. Optional white-balance: gray-world (average R/G and B/G ratios
 *      → multipliers) per Buchsbaum 1980 "A spatial processor model for
 *      object colour perception". Gated behind `opts.whiteBalance`
 *      because gray-world breaks on intentionally-monochrome scenes.
 *
 * All suggestions are returned in the legacy editor's slider-unit
 * conventions (exposure ∈ [-5, +5] EV, contrast/saturation ∈
 * [-100, +100], wb multipliers as [r, g, b] normalised to g=1).
 *
 * @module src/pipeline/auto-tone
 */

/* Rec.709 luminance weights — match the rest of the pipeline. */
const LUMA_R = 0.2126;
const LUMA_G = 0.7152;
const LUMA_B = 0.0722;

/* Target reference values. Tuned to match what `Auto-Tone` should
 * feel like — bright but not blown, contrasty without crushing. */
const TARGET_MEDIAN          = 0.18;   /* Reinhard 2002 mid-grey */
const TARGET_PCT_05          = 0.05;   /* shadows roll-off target */
const TARGET_PCT_95          = 0.85;   /* highlights roll-off target */
const TARGET_MEAN_CHROMA     = 0.15;   /* ColorChecker-24 average */
const HIST_BINS              = 256;
const SAT_SAMPLE_PIXELS      = 1024;

/**
 * Compute the luminance histogram of a Float32 ACEScg RGBA buffer.
 *
 * Pixels with negative or NaN luminance are dropped (post-WB matrix
 * can produce slight negatives at gamut boundaries). The histogram is
 * normalised to a probability mass function (sums to 1.0).
 *
 * @param {Float32Array} rgba   RGBA Float32, length w*h*4
 * @param {number} w
 * @param {number} h
 * @param {object} [opts]
 * @param {number} [opts.bins=256]
 * @returns {{counts:Float64Array, total:number, min:number, max:number}}
 */
export function luminanceHistogram(rgba, w, h, opts) {
    const bins = (opts && opts.bins) || HIST_BINS;
    const counts = new Float64Array(bins);
    let total = 0;
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < rgba.length; i += 4) {
        const y = LUMA_R * rgba[i] + LUMA_G * rgba[i + 1] + LUMA_B * rgba[i + 2];
        if (!(y >= 0)) continue;             /* NaN + negative reject */
        if (y < lo) lo = y;
        if (y > hi) hi = y;
        /* Clamp into [0, 1] for histogram bucketing — values > 1 (HDR)
         * get squashed into the top bin. They're rare for SDR sources
         * and the bias is acceptable for auto-tone. */
        const clipped = y > 1 ? 1 : y;
        const bin = clipped >= 1 ? bins - 1 : Math.floor(clipped * bins);
        counts[bin]++;
        total++;
    }
    if (total > 0) {
        for (let b = 0; b < bins; b++) counts[b] /= total;
    }
    if (!Number.isFinite(lo)) lo = 0;
    if (!Number.isFinite(hi)) hi = 0;
    return { counts, total, min: lo, max: hi };
}

/**
 * Return the value at a given percentile of a normalised histogram.
 * @param {Float64Array} counts
 * @param {number} pct   0..1
 * @returns {number}     percentile value in [0, 1]
 */
export function percentile(counts, pct) {
    const bins = counts.length;
    let cdf = 0;
    for (let b = 0; b < bins; b++) {
        cdf += counts[b];
        if (cdf >= pct) {
            return (b + 0.5) / bins;     /* bin midpoint */
        }
    }
    return 1;
}

/**
 * Compute mean chroma (max - min per pixel) on a stratified sample.
 * Sample-based to keep this cheap even on 8K images.
 */
export function sampleMeanChroma(rgba, w, h, samples) {
    const n = samples || SAT_SAMPLE_PIXELS;
    const px = (w * h) | 0;
    if (px <= 0) return 0;
    const step = Math.max(1, Math.floor(px / n));
    let sum = 0, count = 0;
    for (let p = 0; p < px; p += step) {
        const i = p * 4;
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        if (!(r >= 0 && g >= 0 && b >= 0)) continue;
        const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
        const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
        sum += mx - mn;
        count++;
    }
    return count > 0 ? sum / count : 0;
}

/**
 * Gray-world white-balance multipliers (Buchsbaum 1980). Computes
 * mean R/G and B/G ratios on a stratified sample and returns the
 * inverse so applying the multipliers neutralises the cast.
 */
export function grayWorldWB(rgba, w, h, samples) {
    const n = samples || SAT_SAMPLE_PIXELS;
    const px = (w * h) | 0;
    if (px <= 0) return [1, 1, 1];
    const step = Math.max(1, Math.floor(px / n));
    let sR = 0, sG = 0, sB = 0, cnt = 0;
    for (let p = 0; p < px; p += step) {
        const i = p * 4;
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        if (!(r >= 0 && g >= 0 && b >= 0)) continue;
        sR += r; sG += g; sB += b; cnt++;
    }
    if (cnt === 0 || sG <= 1e-6) return [1, 1, 1];
    const mR = sR / cnt, mG = sG / cnt, mB = sB / cnt;
    /* Multipliers normalised so g stays 1 (preserves exposure). */
    return [mG / mR, 1, mG / mB];
}

/**
 * The entry point. Produces a small suggestion struct usable by the
 * legacy editor's Auto-Tone toolbar action OR returned as JSON for
 * power users.
 *
 * @param {Float32Array} rgba  ACEScg-linear RGBA Float32
 * @param {number} w
 * @param {number} h
 * @param {object} [opts]
 * @param {boolean} [opts.whiteBalance=false]
 * @param {number}  [opts.contrastClamp=80]   max ± contrast suggestion
 * @param {number}  [opts.saturationClamp=60] max ± saturation suggestion
 * @returns {{exposureEV:number, contrast:number, saturation:number,
 *           whiteBalance:[number,number,number]|null,
 *           stats:{p05:number,p50:number,p95:number,p99:number,
 *                  meanChroma:number,histTotal:number}}}
 */
export function suggestAutoTone(rgba, w, h, opts) {
    const o = opts || {};
    const contrastClamp   = Number.isFinite(o.contrastClamp)   ? o.contrastClamp   : 80;
    const saturationClamp = Number.isFinite(o.saturationClamp) ? o.saturationClamp : 60;
    const wantWB = !!o.whiteBalance;

    const { counts, total } = luminanceHistogram(rgba, w, h);
    if (total === 0) {
        return {
            exposureEV: 0, contrast: 0, saturation: 0,
            whiteBalance: wantWB ? [1, 1, 1] : null,
            stats: { p05: 0, p50: 0, p95: 0, p99: 0, meanChroma: 0, histTotal: 0 },
        };
    }
    const p05 = percentile(counts, 0.05);
    const p50 = percentile(counts, 0.50);
    const p95 = percentile(counts, 0.95);
    const p99 = percentile(counts, 0.99);

    /* Exposure: log2 of the ratio between target mid and observed mid.
     * Floor p50 to a small positive so log2 stays finite on pure-black
     * images. Result clamped to [-3, +3] EV (typical Auto-Tone range
     * — bigger swings would be a sign Auto-Tone is the wrong tool). */
    const safeP50 = p50 > 1e-4 ? p50 : 1e-4;
    let exposureEV = Math.log2(TARGET_MEDIAN / safeP50);
    if (exposureEV < -3) exposureEV = -3;
    if (exposureEV >  3) exposureEV =  3;

    /* Contrast: scale slider proportional to how much the observed
     * 5-95 range needs to expand to meet target. After applying the
     * exposure shift, p05 and p95 will be scaled by 2^exposureEV;
     * compute the residual gap. */
    const gain = Math.pow(2, exposureEV);
    const newP05 = p05 * gain;
    const newP95 = p95 * gain;
    const targetSpan = TARGET_PCT_95 - TARGET_PCT_05;
    const observedSpan = Math.max(1e-4, newP95 - newP05);
    /* Linear scale factor 1.0 → no change; >1 means we need more
     * contrast. Map to slider via (factor - 1) * 100, clamped. */
    let contrast = (targetSpan / observedSpan - 1) * 100;
    if (contrast < -contrastClamp) contrast = -contrastClamp;
    if (contrast >  contrastClamp) contrast =  contrastClamp;

    /* Saturation: based on observed mean chroma vs ColorChecker target.
     * Suggestion is a 50-50 mix of "boost-toward-target" and a small
     * additive bump because saturation usually feels muted on screens. */
    const meanChroma = sampleMeanChroma(rgba, w, h);
    let saturation = 0;
    if (meanChroma > 1e-4) {
        const ratio = TARGET_MEAN_CHROMA / meanChroma;
        /* Slider 0 = neutral; positive boosts. Maps factor 1.5 → +25
         * and factor 0.5 → -25. */
        saturation = (ratio - 1) * 50;
    }
    if (saturation < -saturationClamp) saturation = -saturationClamp;
    if (saturation >  saturationClamp) saturation =  saturationClamp;

    const whiteBalance = wantWB ? grayWorldWB(rgba, w, h) : null;

    return {
        exposureEV,
        contrast,
        saturation,
        whiteBalance,
        stats: { p05, p50, p95, p99, meanChroma, histTotal: total },
    };
}

/**
 * Identity check — used by callers that want to detect "already auto-
 * toned" inputs and skip a no-op render. Tolerance covers FP noise.
 */
export function isIdentitySuggestion(s, eps) {
    const e = Number.isFinite(eps) ? eps : 1e-3;
    if (!s) return true;
    return Math.abs(s.exposureEV) < e
        && Math.abs(s.contrast)   < e
        && Math.abs(s.saturation) < e
        && (!s.whiteBalance || (
            Math.abs(s.whiteBalance[0] - 1) < e &&
            Math.abs(s.whiteBalance[2] - 1) < e
        ));
}
